import { API_BASE_URL } from '../config/api';

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
    };
}

export interface DiagnosticCatalogItem {
    id: string;
    practice_id?: string;
    based_on_standard_id?: string;  // If set, this custom item shadows the standard item
    name: string;
    result_type: 'BINARY' | 'NUMERIC' | 'TEXT';
    description?: string;
    default_cost?: number;
    is_active: boolean;
    is_standard: boolean;
    is_stock_tracked?: boolean;
    display_order: number;
    created_at: string;
    updated_at: string;
}

export const getDiagnosticCatalog = async (
    practiceId: string,
    search?: string,
    activeOnly: boolean = true
): Promise<DiagnosticCatalogItem[]> => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('active_only', activeOnly.toString());

    const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/diagnostics?${params.toString()}`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch diagnostics');
    return response.json();
};

export const createDiagnosticCatalogItem = async (
    practiceId: string,
    data: Partial<DiagnosticCatalogItem>
): Promise<DiagnosticCatalogItem> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/diagnostics`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
    });

    if (!response.ok) throw new Error('Failed to create diagnostic');
    return response.json();
};

export const updateDiagnosticCatalogItem = async (
    practiceId: string,
    itemId: string,
    data: Partial<DiagnosticCatalogItem>
): Promise<DiagnosticCatalogItem> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/diagnostics/${itemId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
    });

    if (!response.ok) throw new Error('Failed to update diagnostic');
    return response.json();
};

export const getDiagnosticCatalogItem = async (
    practiceId: string,
    itemId: string
): Promise<DiagnosticCatalogItem> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/diagnostics/${itemId}`, {
        method: 'GET',
        headers: getAuthHeaders()
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch diagnostic' }));
        throw new Error(error.detail || 'Failed to fetch diagnostic');
    }
    return response.json();
};

export const deleteDiagnosticCatalogItem = async (
    practiceId: string,
    itemId: string
): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/diagnostics/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to delete diagnostic');
};

export const hardDeleteDiagnosticCatalogItem = async (
    practiceId: string,
    itemId: string
): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/diagnostics/${itemId}/hard`, {
        method: 'DELETE',
        headers: getAuthHeaders()
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to permanently delete diagnostic');
    }
};
